import { useCallback } from "react";
import Modal from "../../../../../../../../components/Modal/Modal";
import Button from "../../../../../../../../components/Button/Button";
import TrashIcon from "../../../../../../../../assets/svg/trash-icon";
import OpenFolderIcon from "../../../../../../../../assets/svg/open-folder-icon";
import { useDeleteFilesMutation } from "../../../../../../../../redux/services/kbFilesApi";
import { customToast } from "../../../../../../../../utils/custom-toast/custom-toast";
import { isFolder } from "../utils/fileManagement.utils";
import type { DeleteEntityModalProps } from "../types/fileManagement.types";
import {
  DELETE_DESCRIPTION_PREFIX,
  DELETE_ERROR_MESSAGE,
  DELETE_FILE_TITLE,
  DELETE_FILE_DESCRIPTION,
  DELETE_FILE_SUCCESS,
  DELETE_FOLDER_TITLE,
  DELETE_FOLDER_DESCRIPTION,
  DELETE_FOLDER_SUCCESS,
  DELETE_FOLDER_WARNING,
  DELETE_FILE_BUTTON,
  DELETE_FOLDER_BUTTON,
  DELETE_BUTTON_LOADING,
  DELETE_CANCEL_BUTTON,
} from "../constants/fileManagement.constants";

const DeleteEntityModal = ({
  open,
  onOpenChange,
  entity,
}: DeleteEntityModalProps) => {
  const [deleteEntity, { isLoading: isDeleting }] = useDeleteFilesMutation();

  const isEntityFolder = entity ? isFolder(entity) : false;

  const title = isEntityFolder
    ? DELETE_FOLDER_TITLE
    : DELETE_FILE_TITLE;

  const description = isEntityFolder
    ? DELETE_FOLDER_DESCRIPTION
    : DELETE_FILE_DESCRIPTION;

  const successMessage = isEntityFolder
    ? DELETE_FOLDER_SUCCESS
    : DELETE_FILE_SUCCESS;

  const confirmButtonLabel = isEntityFolder ? DELETE_FOLDER_BUTTON : DELETE_FILE_BUTTON;

  const closeModal = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const confirmDelete = useCallback(async () => {
    if (!entity) return;

    try {
      await deleteEntity({ file_ids: [entity.file_id] }).unwrap();
      customToast(successMessage, "success");
      closeModal();
    } catch (error) {
      const errorMessage =
        (error as { data?: { detail?: string } })?.data?.detail ||
        DELETE_ERROR_MESSAGE;
      customToast(errorMessage, "error");
    }
  }, [entity, deleteEntity, closeModal, successMessage]);

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeModal();
      }}
      preventOutsideClick={false}
      customHeader={
        <div className="flex items-center gap-1.5 flex-1">
          <TrashIcon className="w-6 h-6 text-verba-red-alert dark:text-verba-red-alert-dk" />
          <span className="text-heading-text dark:text-heading-text-dk font-medium text-base">
            {title}
          </span>
        </div>
      }
      headerClassName="px-6 pt-[18px] pb-0"
      contentClassName="w-[400px] border border-border-gray dark:border-border-gray-dk bg-fixed-white dark:bg-popper-bg-dk rounded-lg shadow-[10px_10px_60px_0px_rgba(0,0,0,0.2)]"
      overlayClassName="bg-black/80"
      bodyClassName="!p-0"
    >
      <div
        className="flex flex-col px-6 pb-[18px]"
        onClick={(e) => e?.stopPropagation()}
      >
        <p className="text-md font-normal text-dark-gray dark:text-dark-gray-dk mt-3 leading-normal">
          {DELETE_DESCRIPTION_PREFIX}
          {isEntityFolder && (
            <OpenFolderIcon className="inline-block w-[18px] h-[18px] text-analytics-blue dark:text-analytics-blue-dk align-text-bottom mx-0.5" />
          )}
          <span
            className={`${isEntityFolder ? "font-medium" : "font-semibold"} text-heading-text dark:text-heading-text-dk`}
          >
            {entity?.name}
          </span>
          {description}
        </p>

        {isEntityFolder && (
          <div className="mt-3 px-2 py-1.5 rounded-lg border border-card-yellow-stroke dark:border-card-yellow-stroke-dk bg-card-yellow-bg dark:bg-card-yellow-bg-dk text-xs font-normal text-toast-warning dark:text-toast-warning-dk">
            {DELETE_FOLDER_WARNING}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-12">
          <Button
            innerText={DELETE_CANCEL_BUTTON}
            onClick={closeModal}
            disabled={isDeleting}
            buttonContainerClass="min-w-[100px] h-[26px] px-2 bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md cursor-pointer hover:bg-verba-blue dark:hover:bg-verba-blue-dk transition-colors"
            innerTextClass="text-xs font-semibold text-pure-white dark:text-pure-white-dk"
          />
          <Button
            innerText={isDeleting ? DELETE_BUTTON_LOADING : confirmButtonLabel}
            onClick={confirmDelete}
            disabled={isDeleting}
            buttonContainerClass="h-[26px] px-6 border border-verba-red-alert dark:border-verba-red-alert-dk rounded-md cursor-pointer hover:bg-verba-red-alert/10 dark:hover:bg-verba-red-alert-dk/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            innerTextClass="text-xs font-semibold text-verba-red-alert dark:text-verba-red-alert-dk"
          />
        </div>
      </div>
    </Modal>
  );
};

export default DeleteEntityModal;
